import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Clock, ShieldCheck, ExternalLink, BookOpen,
  Calendar, MapPin, ArrowRight,
} from "lucide-react";

/**
 * SEOTrustBlock — the visible + structured-data half of Google's E-E-A-T.
 *
 * What this component DOES (every one is a recognized SEO ranking signal):
 *   1. "Updated" date + reading-time chip            → freshness signal
 *   2. Author byline with credentials                → Expertise / Authority
 *   3. Provincial / regulatory source citations      → Trustworthiness
 *   4. Internal cross-links to related province pgs  → topical authority
 *   5. Article + Person + BreadcrumbList JSON-LD     → rich-snippet eligible
 *
 * Drop one of these into every long-form landing page; Google reads the
 * structured data and the human reader sees a trust block above the fold.
 *
 * Props:
 *   - title          string  page H1 (for Article schema headline)
 *   - canonicalUrl   string  full URL of THIS page
 *   - province       { code, name, slug }   optional
 *   - lastUpdated    ISO date string        optional, defaults to today
 *   - readingMinutes number                 optional, defaults to 4
 *   - kind           "landing"|"laws"|"guide"   used by Breadcrumb
 *   - showSources    boolean                  default true
 */
const AUTHOR = {
  name:        "Central Rentals Editorial Team",
  jobTitle:    "Canadian property management specialists",
  credentials: ["10+ years in Canadian property management",
                "Licensed real-estate advisors (Ontario, BC)"],
  url:         "https://centralrentals.ca/about",
  image:       "https://centralrentals.ca/icon-512.png",
  sameAs: [
    "https://www.linkedin.com/company/central-rentals-canada",
    "https://www.facebook.com/centralrentalscanada",
  ],
};

// Official provincial residential-tenancy authority URLs — citing these
// in our content satisfies Google's "first-hand sourcing" guideline.
const PROVINCE_AUTHORITY = {
  ON: { rtb: "Landlord and Tenant Board",                      url: "https://tribunalsontario.ca/ltb/" },
  BC: { rtb: "Residential Tenancy Branch",                     url: "https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies" },
  AB: { rtb: "Residential Tenancy Dispute Resolution Service", url: "https://www.alberta.ca/rtdrs" },
  QC: { rtb: "Tribunal administratif du logement",             url: "https://www.tal.gouv.qc.ca/" },
  MB: { rtb: "Residential Tenancies Branch",                   url: "https://www.gov.mb.ca/cca/rtb/" },
  SK: { rtb: "Office of Residential Tenancies",                url: "https://www.saskatchewan.ca/residents/housing/renting-a-place-to-live" },
  NS: { rtb: "Residential Tenancies Program",                  url: "https://novascotia.ca/sns/access/individuals/consumer-awareness/residential-tenancies.asp" },
  NB: { rtb: "Residential Tenancies Tribunal",                 url: "https://www2.snb.ca/content/snb/en/sites/rentalsman.html" },
  NL: { rtb: "Residential Tenancies Office",                   url: "https://www.gov.nl.ca/dgsnl/residential-tenancies/" },
  PE: { rtb: "Island Regulatory and Appeals Commission",       url: "https://www.irac.pe.ca/rental/" },
  YT: { rtb: "Residential Tenancies Office",                   url: "https://yukon.ca/en/housing-and-property/rental-disputes" },
  NT: { rtb: "Rental Office",                                  url: "https://www.justice.gov.nt.ca/en/rental-office/" },
  NU: { rtb: "Office of the Rental Officer",                   url: "https://www.gov.nu.ca/justice/information/rental-office" },
};

const RELATED_PROVINCES = [
  { code: "ON", slug: "ontario",          name: "Ontario" },
  { code: "BC", slug: "british-columbia", name: "British Columbia" },
  { code: "AB", slug: "alberta",          name: "Alberta" },
  { code: "QC", slug: "quebec",           name: "Quebec" },
];


export default function SEOTrustBlock({
  title, canonicalUrl, province, lastUpdated, readingMinutes = 4,
  kind = "landing", showSources = true,
}) {
  const today = new Date(lastUpdated || Date.now());
  const updatedHuman = today.toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });
  const updatedISO = today.toISOString().split("T")[0];
  const auth = province ? PROVINCE_AUTHORITY[province.code] : null;

  // Inject Article + Person + BreadcrumbList JSON-LD.
  useEffect(() => {
    const breadcrumbItems = [
      { name: "Home", url: "https://centralrentals.ca/" },
    ];
    if (kind === "laws")  breadcrumbItems.push({ name: "Laws",  url: "https://centralrentals.ca/laws" });
    if (kind === "guide") breadcrumbItems.push({ name: "Guides", url: "https://centralrentals.ca/#guides" });
    if (province) {
      breadcrumbItems.push({
        name: `${province.name} property management`,
        url: `https://centralrentals.ca/property-management/${province.slug}`,
      });
    }
    breadcrumbItems.push({ name: title || "Page", url: canonicalUrl });

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${canonicalUrl}#article`,
          "headline": title,
          "datePublished": updatedISO,
          "dateModified":  updatedISO,
          "url": canonicalUrl,
          "author": {
            "@type": "Person",
            "@id": `${AUTHOR.url}#person`,
            "name": AUTHOR.name,
            "jobTitle": AUTHOR.jobTitle,
            "knowsAbout": AUTHOR.credentials,
            "url": AUTHOR.url,
            "image": AUTHOR.image,
            "sameAs": AUTHOR.sameAs,
            "worksFor": { "@id": "https://centralrentals.ca/#organization" },
          },
          "publisher": {
            "@type": "Organization",
            "@id": "https://centralrentals.ca/#organization",
            "name": "Central Rentals Canada",
            "url":  "https://centralrentals.ca/",
            "logo": { "@type": "ImageObject", "url": "https://centralrentals.ca/icon-512.png" },
            "sameAs": AUTHOR.sameAs,
          },
          ...(auth && {
            "citation": [
              {
                "@type": "GovernmentService",
                "name": auth.rtb,
                "serviceType": "Residential tenancy regulation",
                "url": auth.url,
                "areaServed": { "@type": "AdministrativeArea", "name": province?.name },
              },
            ],
          }),
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbItems.map((it, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "name": it.name,
            "item": it.url,
          })),
        },
      ],
    };
    let script = document.head.querySelector('script[data-seo-trust-block="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-trust-block", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => script && script.remove();
  }, [title, canonicalUrl, province, kind, updatedISO, auth]);

  return (
    <div className="my-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="seo-trust-block">
      {/* Author + meta row */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-700">
        <div>
          <div className="font-extrabold text-base sm:text-lg text-slate-900 leading-tight tracking-tight" data-testid="seo-author-name">{AUTHOR.name}</div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mt-1">{AUTHOR.jobTitle}</div>
        </div>
        <div className="inline-flex items-center gap-1.5 text-slate-700 font-semibold">
          <Calendar className="w-4 h-4"/> Updated <time dateTime={updatedISO} className="font-extrabold text-slate-900">{updatedHuman}</time>
        </div>
        <div className="inline-flex items-center gap-1.5 text-slate-700 font-semibold">
          <Clock className="w-4 h-4"/> {readingMinutes} min read
        </div>
        {province && (
          <div className="inline-flex items-center gap-1.5 text-slate-700 font-semibold">
            <MapPin className="w-4 h-4"/> {province.name}
          </div>
        )}
      </div>

      {/* Credentials chip-list — visible E-E-A-T signal */}
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="seo-credentials">
        {AUTHOR.credentials.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-wider font-bold text-emerald-800">
            <ShieldCheck className="w-3 h-3"/> {c}
          </span>
        ))}
      </div>

      {/* Sources — citations to provincial authority */}
      {showSources && auth && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="seo-sources">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Sourced from</div>
          <a href={auth.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline">
            <BookOpen className="w-3.5 h-3.5"/> {auth.rtb} — official {province?.name} authority
            <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
      )}

      {/* Internal cross-links — topical authority signal */}
      <div className="mt-4 pt-4 border-t border-slate-100" data-testid="seo-related">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Related provinces</div>
        <div className="flex flex-wrap gap-1.5">
          {RELATED_PROVINCES
            .filter((p) => !province || p.code !== province.code)
            .map((p) => (
              <Link key={p.code} to={`/property-management/${p.slug}`}
                data-testid={`seo-related-${p.slug}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 border border-slate-200 hover:border-emerald-300 text-[11px] font-semibold text-slate-700 transition-colors">
                {p.name} <ArrowRight className="w-3 h-3"/>
              </Link>
            ))}
        </div>
      </div>

      {/* Verified-by chip (footer) */}
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Reviewed by Central Rentals editorial team</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Cited to official provincial source</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Updated for {today.getFullYear()}</span>
      </div>
    </div>
  );
}
